import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ChevronRight, Clock, Crown, Trophy, User } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'
import useCountdown from '../hooks/useCountdown'
import { formatClockTimer } from '../utils/rushdropFormat'
import { hapticImpact } from '../../../utils/haptic'

// Интервал обновления me.fast_event_weekly для баннера на главной
const BANNER_POLL_INTERVAL = 30000

/**
 * Баннер недельного розыгрыша на главной странице.
 * Данные — поле me.fast_event_weekly из /api/me/: приходит всем (даже с 0 баллов)
 * и только внутри окна розыгрыша, в остальное время null — тогда баннера нет вовсе.
 * fast_event и fast_event_weekly независимы: в понедельник в 11:00 эвента нет,
 * поэтому на карточку эвента баннер не завязывается.
 * state в баннере — тот же, что на экране розыгрыша; клик ведёт на /rushdrop/weekly.
 */
const RushDropWeeklyBanner = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const weekly = useAppStore((state) => state.user?.fast_event_weekly)
  const loadProfile = useAppStore((state) => state.loadProfile)
  const { playSound } = useAudioManager({ select: 'select' })

  // Окно розыгрыша наступает без действий пользователя — профиль обновляем сами
  useEffect(() => {
    loadProfile().catch(() => {})

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadProfile().catch(() => {})
      }
    }, BANNER_POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [loadProfile])

  const remaining = useCountdown(
    weekly?.state === 'announce' ? weekly?.draw_in_seconds : null
  )

  // Таймер анонса дошёл до нуля — внеочередное обновление, чтобы подхватить drawing
  useEffect(() => {
    if (weekly?.state === 'announce' && remaining === 0) {
      const timeout = setTimeout(() => loadProfile().catch(() => {}), 1000)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [weekly?.state, remaining, loadProfile])

  if (!weekly) return null

  const { state, title, skin, winner } = weekly

  const handleOpen = () => {
    playSound('select')
    hapticImpact('light')
    navigate('/rushdrop/weekly')
  }

  // Подпись статуса по state (state и есть источник правды, не таймер)
  const statusLabel = state === 'announce'
    ? t('rushdrop.weeklyDraw.announce', 'Скоро розыгрыш')
    : state === 'drawing'
      ? t('rushdrop.weeklyDraw.drawing', 'Идёт розыгрыш')
      : t('rushdrop.weeklyDraw.results', 'Результаты')

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35 }}
      onClick={handleOpen}
      className="relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border border-yellow-400/30 bg-gradient-to-r from-yellow-400/15 via-surface to-orange-500/10 p-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] transition-all active:scale-[0.98]"
    >
      {/* Приз недели */}
      {skin?.image ? (
        <img
          src={skin.image}
          alt={skin.name || ''}
          className="h-14 w-14 shrink-0 object-contain"
          loading="lazy"
        />
      ) : (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-yellow-400/10">
          <Crown size={24} className="text-yellow-400" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <Crown size={12} className="shrink-0 text-yellow-400" />
          <p className="text-[11px] font-bold uppercase tracking-wide text-yellow-400">
            {t('rushdrop.weeklyDraw.badge', 'Розыгрыш недели')}
          </p>
        </div>
        {/* title заполнен только в announce; в drawing/results — своя подпись */}
        <p className="truncate text-sm font-semibold text-white">
          {title || statusLabel}
        </p>
        <p className={`mt-0.5 flex items-center gap-1 text-xs ${state === 'drawing' ? 'animate-pulse text-yellow-300' : 'text-text-secondary'}`}>
          {state === 'announce' && (
            <>
              <Clock size={12} className="shrink-0 text-yellow-400" />
              <b className="tabular-nums text-yellow-300">
                {remaining > 0
                  ? formatClockTimer(remaining)
                  : t('rushdrop.weeklyDraw.drawing', 'Идёт розыгрыш')}
              </b>
            </>
          )}
          {state === 'drawing' && statusLabel}
          {state === 'results' && (
            winner ? (
              <>
                <Trophy size={12} className="shrink-0 text-yellow-400" />
                <span className="truncate">{winner.name}</span>
                {Number(winner.you) === 1 && (
                  <b className="shrink-0 text-accent-primary">
                    {t('rushdrop.weeklyDraw.youWon', 'Ты выиграл!')}
                  </b>
                )}
              </>
            ) : (
              // Приз сгорел — победителя нет
              <span className="flex items-center gap-1">
                <User size={12} className="shrink-0" />
                {t('rushdrop.weeklyDraw.burned', 'Победителя нет — приз сгорел')}
              </span>
            )
          )}
        </p>
      </div>

      <ChevronRight size={16} className="shrink-0 text-yellow-400" />
    </motion.button>
  )
}

export default RushDropWeeklyBanner
